import { Group } from '@visx/group';
import {
  MARKERS_OFFSET,
  MARKER_COLORS_CSS_VARS,
  MARKER_LINE_WIDTH,
  getMarkerBoxWidth,
  measureBoxOverlap,
} from '../utils';
import type { ScaleLinear } from 'd3-scale';
import isNumber from 'lodash/isNumber';
import { FairValueHistogramChartMarker } from './FairValueHistogramChartMarker';
import { formatterCurrencyTruncated } from '@/utilities/format';
import type { CurrencyISO } from '@/constants/currencies';
import { useMemo } from 'react';

type MarkerInput = {
  label: string;
  value: string;
  x: number;
  accentColor: string;
  boxWidth: number;
};

type MarkerOutput = MarkerInput & {
  yOffset: number;
  boxStartX: number;
};

type Props = {
  xScale: ScaleLinear<number, number, never>;
  y: number;
  currencyIso: CurrencyISO;
  fairValue: number | undefined | null;
  lastSharePrice: number | undefined | null;
};

export const FairValueHistogramChartMarkers = ({
  xScale,
  y,
  fairValue,
  lastSharePrice,
  currencyIso,
}: Props) => {
  const [, xMax] = xScale.range();
  const markerInputs = useMemo(() => {
    const inputs: MarkerInput[] = [];
    if (isNumber(lastSharePrice)) {
      const label = 'Last Share Price';
      const value = formatterCurrencyTruncated(lastSharePrice, currencyIso, 2);
      inputs.push({
        label,
        value,
        x: xScale(lastSharePrice),
        accentColor: MARKER_COLORS_CSS_VARS.SHARE_PRICE,
        boxWidth: getMarkerBoxWidth(label, value).boxWidth,
      });
    }
    if (isNumber(fairValue)) {
      const label = 'My Fair Value';
      const value = formatterCurrencyTruncated(fairValue, currencyIso, 2);
      inputs.push({
        label,
        value,
        x: xScale(fairValue),
        accentColor: MARKER_COLORS_CSS_VARS.MY_FAIR_VALUE,
        boxWidth: getMarkerBoxWidth(label, value).boxWidth,
      });
    }
    return inputs;
  }, [xScale, lastSharePrice, fairValue, currencyIso]);

  const markerOutputs: MarkerOutput[] = useMemo(
    () =>
      markerInputs.reduce((result, markerInput) => {
        const previousMarker = result.at(-1);

        const { x, boxWidth } = markerInput;

        const isFlipped = x + boxWidth > xMax;
        const boxStartX = isFlipped ? x - boxWidth : x;
        const overlapDistance = previousMarker
          ? measureBoxOverlap(
              {
                x: previousMarker.boxStartX,
                width: previousMarker.boxWidth + MARKER_LINE_WIDTH,
              },
              {
                x: boxStartX,
                width: boxWidth + MARKER_LINE_WIDTH,
              }
            )
          : 0;

        const yOffset =
          overlapDistance && previousMarker
            ? previousMarker.yOffset + MARKERS_OFFSET
            : MARKERS_OFFSET;

        return [
          ...result,
          {
            ...markerInput,
            x: Math.min(x, xMax - MARKER_LINE_WIDTH),
            boxStartX,
            boxWidth,
            yOffset,
          },
        ];
      }, [] as MarkerOutput[]),
    [markerInputs, xMax]
  );

  return (
    <Group top={y}>
      {markerOutputs.map((marker, index) => (
        <rect
          key={index}
          x={marker.x}
          y={0}
          width={MARKER_LINE_WIDTH}
          height={marker.yOffset}
          fill="var(--s-color-surface-3)"
        />
      ))}
      {markerOutputs.map((marker) => (
        <FairValueHistogramChartMarker
          key={marker.label}
          x={marker.x}
          y={marker.yOffset}
          label={marker.label}
          value={marker.value}
          accentColor={marker.accentColor}
          boxStartX={marker.boxStartX}
        />
      ))}
    </Group>
  );
};
